// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';

import { ActionType } from 'flow/store/engine/constants';
import { BEST_EFFORT_PARSING_DEFAULT_STRING, getThresholdLeftDataType, isThresholdUsingNullOperator } from 'flow/service/thresholds';
import { ThresholdContext } from '../context';
import RuleThresholdDropdown from '../RuleThresholdDropdown';
import { RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { StyledPre } from '../../../styles';
import { UserContext } from '~/dashboard/context';


function EditableThresholdValue({
  rulePath,
  rulePathIndex,
}: {
  rulePath: string;
  rulePathIndex: number;
}) {
  const { threshold } = useContext(ThresholdContext);

  const { featureFlags } = useContext(UserContext);

  // Left action/attribute datatype is very important to how this component renders
  // Attribute defaults to first since not all actions have individual attributes
  const dataType = getThresholdLeftDataType(threshold);
  const attributeValues = threshold.left.attribute?.values
    ? threshold.left.attribute?.values.split(',')
    : [];

  const stringValue = threshold.right.attributeValue === BEST_EFFORT_PARSING_DEFAULT_STRING
    ? '' : String(threshold.right.attributeValue);
  const valueIsRange = threshold.operator === 'range' && stringValue.includes(',');
  const rulePathWithIndex = useMemo(() => (`${rulePath}[${rulePathIndex}]`), [rulePath, rulePathIndex]);

  const getRuleThresholdDropdown = (mode: RuleEditorDropdownMode, pretext?: string) => (
    <>
      {pretext && <StyledPre>{`${pretext}`}</StyledPre>}
      <RuleThresholdDropdown
        parentRulePath={rulePathWithIndex}
        mode={mode}
      />
    </>
  );

  // eslint-disable-next-line consistent-return
  const renderRightAttributeValueDropdown = (): (JSX.Element | void) => {
    switch (dataType) {
      case 'string': {
        return attributeValues.length > 0
          ? getRuleThresholdDropdown(RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT, 'is')
          : getRuleThresholdDropdown(RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE);
      }
      case 'boolean': {
        return getRuleThresholdDropdown(
          RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_BOOLEAN,
        );
      }
      case 'integer':
      case 'decimal':
      case 'date':
      case 'timestamp': {
        /* These are the only data types to support static ranges of values */
        return valueIsRange
          ? (
            <>
              {getRuleThresholdDropdown(
                RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MIN,
              )}
              {getRuleThresholdDropdown(
                RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX, 'to',
              )}
            </>
          )
          : getRuleThresholdDropdown(RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE);
      }
    }
  };

  const rightAttrValueAvailableForRendering = () => {
    const isNullAttribute = isThresholdUsingNullOperator({
      leftDataType: dataType,
      threshold,
      supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
    });

    return !isNullAttribute;
  };

  return (
    <>
      {rightAttrValueAvailableForRendering() && renderRightAttributeValueDropdown()}

      {dataType === 'reasoncodes'
        && getRuleThresholdDropdown(
          RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP,
        )}

      {(
        threshold.left.action.type === ActionType.TAG
      ) && getRuleThresholdDropdown(
        RuleEditorDropdownMode.THRESHOLD_RIGHT_TAGACTIONRESULT,
      )}

      {(
        threshold.left.action.type === ActionType.OUTCOME_REASON
      ) && getRuleThresholdDropdown(
        RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE,
      )}
    </>
  );
}

export default EditableThresholdValue;
